import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Trash2, Inbox, Save, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import Logo from "@/components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-navy focus:border-royal focus:outline-none focus:ring-2 focus:ring-royal/20";

const PROBLEM_ICONS = ["Filter", "AlarmClock", "Layers"];
const SOLUTION_ICONS = ["SquareKanban", "UserSearch", "Send", "BarChart3", "Blocks", "Bot"];
const WHY_ICONS = ["Workflow", "Users", "Code2", "ShieldCheck"];

const LoginCard = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await login(email, password);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
            <Logo />
            <h1 className="mt-6 font-display text-2xl font-extrabold text-navy">Admin Studio</h1>
            <p className="mt-1 text-sm text-slate-500">Sign in to edit site content and view enquiries.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                    <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Email</label>
                    <input id="admin-email" data-testid="admin-email-input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
                </div>
                <div>
                    <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
                    <input id="admin-password" data-testid="admin-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} required />
                </div>
                {error && <p data-testid="admin-login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{error}</p>}
                <button type="submit" data-testid="admin-login-button" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-royal disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in
                </button>
            </form>
            <Link to="/" data-testid="admin-back-home-link" className="mt-5 block text-center text-sm font-medium text-slate-500 hover:text-royal">
                Back to website
            </Link>
        </div>
    );
};

const Field = ({ label, value, onChange, textarea = false, testId }) => (
    <div>
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
        {textarea ? (
            <textarea data-testid={testId} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-none`} />
        ) : (
            <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
        )}
    </div>
);

const IconSelect = ({ label, value, onChange, options, testId }) => (
    <div>
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
        <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
            {options.map((o) => (
                <option key={o} value={o}>{o}</option>
            ))}
        </select>
    </div>
);

const ObjectList = ({ items, fields, onChange, testPrefix, template, addLabel }) => {
    const update = (i, key, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
    return (
        <div className="space-y-4">
            {items.map((item, i) => (
                <div key={i} className="space-y-3 rounded-xl border border-slate-200 bg-mist p-4">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Item {i + 1}</span>
                        <button type="button" data-testid={`${testPrefix}-remove-${i}`} aria-label="Remove item" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                    {fields.map((f) =>
                        f.type === "icon" ? (
                            <IconSelect key={f.key} label={f.label} value={item[f.key]} options={f.options} onChange={(v) => update(i, f.key, v)} testId={`${testPrefix}-${i}-${f.key}`} />
                        ) : (
                            <Field key={f.key} label={f.label} value={item[f.key]} textarea={f.textarea} onChange={(v) => update(i, f.key, v)} testId={`${testPrefix}-${i}-${f.key}`} />
                        )
                    )}
                </div>
            ))}
            <button type="button" data-testid={`${testPrefix}-add`} onClick={() => onChange([...items, { ...template }])} className="inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-500 hover:border-royal hover:text-royal">
                <Plus className="h-4 w-4" /> {addLabel}
            </button>
        </div>
    );
};

const LinesField = ({ label, value, onChange, testId }) => (
    <div>
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
        <textarea data-testid={testId} rows={Math.max(3, value.length + 1)} value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className={`${inputCls} resize-none font-mono text-xs`} />
        <p className="mt-1 text-xs text-slate-400">One entry per line.</p>
    </div>
);

const ContentEditor = () => {
    const { content, save } = useSite();
    const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content)));
    const [busy, setBusy] = useState(false);

    const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await save(draft);
            toast.success("All site content updated — live on the page now.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const resetAll = async () => {
        setBusy(true);
        try {
            const r = await api.post("/content/reset");
            toast.info("Reset to the original defaults. Click Save to publish.");
            setDraft(JSON.parse(JSON.stringify({ ...content, ...r.data })));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form data-testid="admin-content-form" onSubmit={submit}>
            <Accordion type="single" collapsible className="space-y-3">
                <AccordionItem value="hero" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-hero" className="font-display font-bold text-navy hover:no-underline">Hero</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Eyebrow" value={draft.hero.eyebrow} onChange={(v) => set("hero")({ ...draft.hero, eyebrow: v })} testId="edit-hero-eyebrow" />
                        <Field label="Headline" value={draft.hero.headline} onChange={(v) => set("hero")({ ...draft.hero, headline: v })} testId="edit-hero-headline" />
                        <Field label="Subhead" textarea value={draft.hero.subhead} onChange={(v) => set("hero")({ ...draft.hero, subhead: v })} testId="edit-hero-subhead" />
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Primary CTA" value={draft.hero.primaryCta} onChange={(v) => set("hero")({ ...draft.hero, primaryCta: v })} testId="edit-hero-primary" />
                            <Field label="Secondary CTA" value={draft.hero.secondaryCta} onChange={(v) => set("hero")({ ...draft.hero, secondaryCta: v })} testId="edit-hero-secondary" />
                        </div>
                        <LinesField label="Micro-trust bullets" value={draft.hero.microTrust} onChange={(v) => set("hero")({ ...draft.hero, microTrust: v })} testId="edit-hero-microtrust" />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="contact" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-contact" className="font-display font-bold text-navy hover:no-underline">Contact details</AccordionTrigger>
                    <AccordionContent className="grid gap-3 pb-5 sm:grid-cols-2">
                        <Field label="Email" value={draft.contact.email} onChange={(v) => set("contact")({ ...draft.contact, email: v })} testId="edit-contact-email" />
                        <Field label="Phone (display)" value={draft.contact.phone} onChange={(v) => set("contact")({ ...draft.contact, phone: v })} testId="edit-contact-phone" />
                        <Field label="WhatsApp number (digits, with country code)" value={draft.contact.whatsapp} onChange={(v) => set("contact")({ ...draft.contact, whatsapp: v })} testId="edit-contact-whatsapp" />
                        <Field label="LinkedIn URL" value={draft.contact.linkedin} onChange={(v) => set("contact")({ ...draft.contact, linkedin: v })} testId="edit-contact-linkedin" />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="trust" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-trust" className="font-display font-bold text-navy hover:no-underline">Trust bar</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Label" value={draft.trust.label} onChange={(v) => set("trust")({ ...draft.trust, label: v })} testId="edit-trust-label" />
                        <LinesField label="Client logos (text)" value={draft.trust.logos} onChange={(v) => set("trust")({ ...draft.trust, logos: v })} testId="edit-trust-logos" />
                        <ObjectList items={draft.trust.stats} onChange={(v) => set("trust")({ ...draft.trust, stats: v })} testPrefix="edit-trust-stat" template={{ value: "", label: "" }} addLabel="Add stat"
                            fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="problems" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-problems" className="font-display font-bold text-navy hover:no-underline">Problem cards</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.problems.heading} onChange={(v) => set("problems")({ ...draft.problems, heading: v })} testId="edit-problems-heading" />
                        <Field label="Subheading" textarea value={draft.problems.sub} onChange={(v) => set("problems")({ ...draft.problems, sub: v })} testId="edit-problems-sub" />
                        <ObjectList items={draft.problems.items} onChange={(v) => set("problems")({ ...draft.problems, items: v })} testPrefix="edit-problem" template={{ icon: "Filter", title: "", text: "" }} addLabel="Add problem card"
                            fields={[{ key: "icon", label: "Icon", type: "icon", options: PROBLEM_ICONS }, { key: "title", label: "Title" }, { key: "text", label: "Text", textarea: true }]} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="solutions" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-solutions" className="font-display font-bold text-navy hover:no-underline">Solutions</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.solutions.heading} onChange={(v) => set("solutions")({ ...draft.solutions, heading: v })} testId="edit-solutions-heading" />
                        <Field label="Subheading" textarea value={draft.solutions.sub} onChange={(v) => set("solutions")({ ...draft.solutions, sub: v })} testId="edit-solutions-sub" />
                        <ObjectList items={draft.solutions.items} onChange={(v) => set("solutions")({ ...draft.solutions, items: v })} testPrefix="edit-solution" template={{ icon: "Blocks", title: "", text: "" }} addLabel="Add solution card"
                            fields={[{ key: "icon", label: "Icon", type: "icon", options: SOLUTION_ICONS }, { key: "title", label: "Title" }, { key: "text", label: "Text", textarea: true }]} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="steps" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-steps" className="font-display font-bold text-navy hover:no-underline">How it works</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.steps.heading} onChange={(v) => set("steps")({ ...draft.steps, heading: v })} testId="edit-steps-heading" />
                        <Field label="Subheading" textarea value={draft.steps.sub} onChange={(v) => set("steps")({ ...draft.steps, sub: v })} testId="edit-steps-sub" />
                        <ObjectList items={draft.steps.items} onChange={(v) => set("steps")({ ...draft.steps, items: v })} testPrefix="edit-step" template={{ step: "05", title: "", text: "" }} addLabel="Add step"
                            fields={[{ key: "step", label: "Step number" }, { key: "title", label: "Title" }, { key: "text", label: "Text", textarea: true }]} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="cases" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-cases" className="font-display font-bold text-navy hover:no-underline">Case studies</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.cases.heading} onChange={(v) => set("cases")({ ...draft.cases, heading: v })} testId="edit-cases-heading" />
                        <Field label="Subheading" textarea value={draft.cases.sub} onChange={(v) => set("cases")({ ...draft.cases, sub: v })} testId="edit-cases-sub" />
                        <ObjectList items={draft.cases.items} onChange={(v) => set("cases")({ ...draft.cases, items: v })} testPrefix="edit-case" template={{ tag: "SAMPLE", company: "", size: "", challenge: "", solution: "", result: "", metric: "", metricLabel: "" }} addLabel="Add case study"
                            fields={[
                                { key: "tag", label: "Tag" }, { key: "company", label: "Company" }, { key: "size", label: "Size" },
                                { key: "challenge", label: "Challenge", textarea: true }, { key: "solution", label: "Solution", textarea: true }, { key: "result", label: "Result", textarea: true },
                                { key: "metric", label: "Metric" }, { key: "metricLabel", label: "Metric label" },
                            ]} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="why" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-why" className="font-display font-bold text-navy hover:no-underline">Why Bangde + founder</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.why.heading} onChange={(v) => set("why")({ ...draft.why, heading: v })} testId="edit-why-heading" />
                        <Field label="Subheading" textarea value={draft.why.sub} onChange={(v) => set("why")({ ...draft.why, sub: v })} testId="edit-why-sub" />
                        <ObjectList items={draft.why.points} onChange={(v) => set("why")({ ...draft.why, points: v })} testPrefix="edit-why-point" template={{ icon: "ShieldCheck", title: "", text: "" }} addLabel="Add differentiator"
                            fields={[{ key: "icon", label: "Icon", type: "icon", options: WHY_ICONS }, { key: "title", label: "Title" }, { key: "text", label: "Text", textarea: true }]} />
                        <Field label="Quote" textarea value={draft.why.founder.quote} onChange={(v) => set("why")({ ...draft.why, founder: { ...draft.why.founder, quote: v } })} testId="edit-founder-quote" />
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Leader name" value={draft.why.founder.name} onChange={(v) => set("why")({ ...draft.why, founder: { ...draft.why.founder, name: v } })} testId="edit-founder-name" />
                            <Field label="Leader role" value={draft.why.founder.role} onChange={(v) => set("why")({ ...draft.why, founder: { ...draft.why.founder, role: v } })} testId="edit-founder-role" />
                        </div>
                        <Field label="Leader photo URL (optional — leave blank for initials badge)" value={draft.why.founder.photo || ""} onChange={(v) => set("why")({ ...draft.why, founder: { ...draft.why.founder, photo: v } })} testId="edit-founder-photo" />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-faq" className="font-display font-bold text-navy hover:no-underline">FAQ</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.faq.heading} onChange={(v) => set("faq")({ ...draft.faq, heading: v })} testId="edit-faq-heading" />
                        <ObjectList items={draft.faq.items} onChange={(v) => set("faq")({ ...draft.faq, items: v })} testPrefix="edit-faq" template={{ q: "", a: "" }} addLabel="Add question"
                            fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", textarea: true }]} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="finalcta" className="rounded-xl border border-slate-200 px-5">
                    <AccordionTrigger data-testid="editor-section-finalcta" className="font-display font-bold text-navy hover:no-underline">Final CTA + form</AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-5">
                        <Field label="Heading" value={draft.finalCta.heading} onChange={(v) => set("finalCta")({ ...draft.finalCta, heading: v })} testId="edit-finalcta-heading" />
                        <Field label="Subheading" textarea value={draft.finalCta.sub} onChange={(v) => set("finalCta")({ ...draft.finalCta, sub: v })} testId="edit-finalcta-sub" />
                        <LinesField label="Team size options" value={draft.finalCta.teamSizes} onChange={(v) => set("finalCta")({ ...draft.finalCta, teamSizes: v })} testId="edit-finalcta-teamsizes" />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            <div className="sticky bottom-0 mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white py-4">
                <button type="submit" data-testid="admin-save-button" disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-amberct px-6 py-3 text-sm font-bold text-navy transition-colors hover:bg-amber-400 disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all changes
                </button>
                <button type="button" data-testid="admin-reset-button" onClick={resetAll} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-600 hover:border-red-300 hover:text-red-600 disabled:opacity-60">
                    <RotateCcw className="h-4 w-4" /> Load defaults
                </button>
            </div>
        </form>
    );
};

const EnquiriesPanel = () => {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/enquiries")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    }, []);

    const remove = async (id) => {
        await api.delete(`/enquiries/${id}`);
        setItems((prev) => prev.filter((x) => x.enquiry_id !== id));
        toast.success("Enquiry deleted.");
    };

    if (items === null) {
        return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-royal" /></div>;
    }

    if (items.length === 0) {
        return (
            <div data-testid="enquiries-empty" className="flex flex-col items-center py-16 text-center">
                <Inbox className="h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-500">No enquiries yet. New discovery-call requests land here and in your inbox.</p>
            </div>
        );
    }

    return (
        <ul data-testid="enquiries-list" className="space-y-4">
            {items.map((item) => (
                <li key={item.enquiry_id} data-testid={`enquiry-${item.enquiry_id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="font-display text-base font-bold text-navy">{item.name} <span className="font-sans text-sm font-medium text-slate-400">· {item.company || "—"}</span></p>
                            <p className="mt-0.5 text-xs text-slate-400">{item.email} {item.phone ? `· ${item.phone}` : ""} {item.service ? `· ${item.service}` : ""}</p>
                        </div>
                        <button type="button" data-testid={`enquiry-delete-${item.enquiry_id}`} onClick={() => remove(item.enquiry_id)} aria-label="Delete enquiry" className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{item.message}</p>
                    <p className="mt-3 text-[11px] uppercase tracking-wider text-slate-400">{new Date(item.created_at).toLocaleString("en-IN")}</p>
                </li>
            ))}
        </ul>
    );
};

const Studio = () => {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("enquiries");

    return (
        <div className="mx-auto w-full max-w-4xl">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold text-navy sm:text-3xl">Admin Studio</h1>
                    <p className="mt-1 text-sm text-slate-500">Signed in as {user.email}</p>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/" data-testid="admin-view-site-link" className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-navy hover:border-royal hover:text-royal">
                        View site
                    </Link>
                    <button type="button" data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-xl bg-navy px-4 py-2.5 text-sm font-bold text-white hover:bg-royal">
                        <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
            </div>

            <div className="mt-8 flex gap-2 border-b border-slate-200">
                {[
                    { id: "enquiries", label: "Enquiries" },
                    { id: "content", label: "Site Content" },
                ].map((t) => (
                    <button key={t.id} type="button" data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`rounded-t-xl px-5 py-3 text-sm font-bold transition-colors ${tab === t.id ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="rounded-b-2xl rounded-tr-2xl bg-white p-6 shadow-sm sm:p-8">
                {tab === "enquiries" ? <EnquiriesPanel /> : <ContentEditor />}
            </div>
        </div>
    );
};

const Admin = () => {
    const { user } = useAuth();

    return (
        <div data-testid="admin-page" className="flex min-h-screen flex-col bg-mist px-4 py-10 sm:px-6">
            {user === null ? (
                <div className="flex flex-1 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-royal" /></div>
            ) : user === false ? (
                <div className="flex flex-1 items-center justify-center"><LoginCard /></div>
            ) : (
                <Studio />
            )}
        </div>
    );
};

export default Admin;
